/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, {useState, useEffect, useRef} from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isLoggedIn, userToken, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import {Trans,  useTranslation } from 'react-i18next';
import {blockedSessionRoute, checkApiSessionErrorMessage, checkIsTokenValid, checkValidToken, getLang, landAfterLogin} from "../../Helper"
import { useRetryCountdown } from "../../hooks/useRetryCountdown";
import RetryNotice from "../widgets/RetryNotice";
import { cancelPasskeyCeremony, passkeyErrorMessage, passkeysSupported, registerPasskey } from "../../Passkey";

import Frame from '../widgets/Frame';
import VoiceCallNotice from "../widgets/VoiceCallNotice";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import RecoveryCodes from "../widgets/RecoveryCodes";
import { useWebOtp } from "../../hooks/useWebOtp";
import { isFeatureOn } from "../../portalSettings";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

// Authenticator apps use a secret instead of a destination.
const APP_METHOD = 'APP';

// A passkey has no destination and no code: the browser's own prompt is the
// whole of the enrolment, so it never reaches the verify step below.
const PASSKEY_METHOD = 'PASSKEY';

// שיחה. הקוד שלה אינו נשלח אלא מוצג על הצג, ולכן שלב ההקלדה שלה נראה אחרת.
const VOICE_METHOD = 'VOICE';

// מסרון - השיטה היחידה כאן שהדפדפן יכול למלא את הקוד שלה לבד. ראה `useWebOtp`.
const SMS_METHOD = 'SMS';

const PHASE_CHOOSE = 'choose';
const PHASE_VERIFY = 'verify';
const PHASE_RECOVERY = 'recovery';

/**
 * התוויות של האפשרויות, והיעד **מבודד בכיווניות**.
 *
 * `<span>` סביב `{{destination}}` ולא הכנסה ישירה למשפט: המשפט עברי והיעד אינו -
 * מספר טלפון כמו `+972-50-...` בתוך טקסט RTL מוצג עם ה-`+` בקצה הלא נכון, והמקטעים
 * שמופרדים במקף מתחלפים במקומם. זו אותה תבנית שכבר בשימוש ב-`LoginVerify` (`<Trans>`
 * עם `components={{span: <span className="ltr-span" />}}`), ולא מנגנון חדש.
 *
 * `APP` ו-`PASSKEY` בלי `<span>` כי אין להם יעד - אין מה לבודד.
 */
const METHOD_LABEL = {
    SMS: "Text message to <span>{{destination}}</span>",
    VOICE: "Voice call to <span>{{destination}}</span>",
    EMAIL: "Email to <span>{{destination}}</span>",
    APP: "Authenticator App",
    PASSKEY: "Passkey (fingerprint, face or security key)",
};

/** הרכיב שהיעד נעטף בו. אחד, כדי שכל השלוש ייראו אותו דבר. */
const LTR_VALUE = { span: <span className="ltr-span" /> };

export function TwoStepVerification() {
    const [state, setState] = useState({
        phase: PHASE_CHOOSE,
        options: [],
        unavailable: [],
        selected: null,
        methodUuid: "",
        qrCode: "",
        code: "",
        recoveryCodes: [],
        // Prevent duplicate requests.
        submitting: false,
        errors: {},
        message: null,
    });


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const isUserLoggedIn = useSelector(isLoggedIn);
    const reseller = useSelector((state) => state.reseller.value);

    /**
     * המסך הזה משרת שני מסלולים, וזה מה שמפריד ביניהם.
     *
     *   ההרשמה         `/create-credentials` → כאן → `/congratulations`
     *   אימות חובה     `/login` → כאן → הממשק
     *
     * `afterLogin` נמסר מ-`Login` ולא נגזר מהטוקן, כי מהטוקן אין דרך לדעת: בשני
     * המסלולים הוא סשן מחובר תקף.
     */
    const afterLogin = location.state?.afterLogin === true;

    /**
     * האם הריסיילר מחייב אימות דו-שלבי - ולכן אין "דלג".
     *
     * **המסך אינו השער.** מי שידלג בכל זאת - בניווט ישיר, או בקליינט ישן - מקבל סשן
     * שאינו פותח דבר: השער בשרת דוחה כל בקשה לכל שירות עד שנרשמה שיטה (ראה
     * `TwoFactorPolicy` ב-SSO). מה שנקבע כאן הוא רק אם להציע את הכפתור, כי כפתור
     * שמוביל למסך חסום גרוע מהיעדרו.
     *
     * `isFeatureOn` עם ברירת מחדל `true`: זו ברירת המחדל בקטלוג, וגם התשובה הנכונה
     * כשההגדרה לא הגיעה - מסך שמציע "דלג" למי שלא יוכל לדלג הוא מסך שמשקר.
     */
    const require2fa = isFeatureOn(reseller, 'require2fa');

    // Sending and submitting codes have separate rate limits.
    const sendRetry = useRetryCountdown();
    const verifyRetry = useRetryCountdown();

    /*
     * הסמן בשדה הקוד ברגע שהקוד יוצא - כלומר במעבר ל-`PHASE_VERIFY`.
     *
     * **חוץ מיישומון האימות.** שם מעל השדה יש קוד QR שצריך לסרוק, ופוקוס במובייל
     * פותח מקלדת שמכסה בדיוק אותו. בשיטות שנשלחות אין מה לראות מעל השדה.
     */
    const [codeRef, focusCode] = useAutoFocus(
        state.phase === PHASE_VERIFY && state.selected?.methodType !== APP_METHOD ? PHASE_VERIFY : null
    );

    /**
     * מילוי אוטומטי של הקוד, כשהשיטה הנרשמת היא מסרון.
     *
     * זהו אשף ההתקנה, כלומר המסרון הראשון שיוצא למספר שהלקוח הרגע הקליד - ובדיוק שם
     * ההעתקה ידנית היא המכשול: הלקוח נמצא באמצע הרשמה ועובר לאפליקציית ההודעות.
     */
    useWebOtp(
        state.phase === PHASE_VERIFY && state.selected?.methodType === SMS_METHOD,
        (code) => setState((prev) => ({ ...prev, code, errors: {} })),
        () => handleVerifyClick()
    );

    // The ref blocks same-tick double clicks; state controls the disabled styling.
    const submittingRef = useRef(false);

    const beginSubmit = () => {
        if (submittingRef.current) {
            return false;
        }
        submittingRef.current = true;
        setState((prev) => ({...prev, submitting: true}));
        return true;
    };

    const endSubmit = () => {
        submittingRef.current = false;
        setState((prev) => ({...prev, submitting: false}));
    };

    const BLOCKED_STYLE = {opacity: 0.5, pointerEvents: "none"};

    // Navigation controls are blocked only while a request is in flight.
    const inFlightStyle = state.submitting ? BLOCKED_STYLE : undefined;

    // Sending and verification use their respective rate limits.
    const sendingStyle = state.submitting || sendRetry.isWaiting ? BLOCKED_STYLE : undefined;
    const verifyingStyle = state.submitting || verifyRetry.isWaiting ? BLOCKED_STYLE : undefined;

    useEffect(()=>{
        if(isUserLoggedIn){
            checkValidToken(navigate, dispatch, logout, false, true, "/login");
            getEnrollmentOptions();
        } else {
            navigate("/login");
        }
        // eslint-disable-next-line
    },[]);

    const requestConfig = () => {
        let config = {'headers': {"token": userToken(), "lang": getLang()}};
        return config;
    };

    const showReplyError = (reply, retryTarget = null) => {
        let errorMessage = DEFAULT_ERROR_TEXT;
        if((reply?.data?.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
            errorMessage = reply.data["message"];
        }
        if(checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
            return false;
        }

        // The server reports lockouts in fractional minutes.
        if(retryTarget && reply?.data?.hasOwnProperty("tryAgainInMinutes")){
            retryTarget.startFromMinutes(reply.data["tryAgainInMinutes"]);
        }

        setState((prev) => ({...prev, errors: {server_error: errorMessage}, message: null}));
        return true;
    };

    const showRequestError = () => {
        setState((prev) => ({...prev, errors: {server_error: DEFAULT_ERROR_TEXT}, message: null}));
    };

    const getEnrollmentOptions = async () => {
        try {
            const reply = await axios.get(API_URL + 'get2faEnrollmentOptions', requestConfig());
            if(reply?.data?.responseStatus === API_OK){
                /*
                 * A passkey the browser cannot create is not an option.
                 *
                 * The server offers it to everyone — it has no way to know what
                 * is in front of it — so the filter belongs here, where the
                 * answer is actually known. Offering it anyway would put a row
                 * on the screen whose only outcome is an error.
                 */
                const options = (reply.data.options ?? []).filter(
                    (option) => option.methodType !== PASSKEY_METHOD || passkeysSupported()
                );
                setState((prev) => ({
                    ...prev,
                    options: options,
                    unavailable: reply.data.unavailable ?? [],
                    selected: options.length > 0 ? options[0] : null,
                    errors: {},
                }));
                return;
            }
            showReplyError(reply);
        } catch (e) {
            showRequestError();
        }
    };

    const selectOption = (option) => {
        sendRetry.reset();
        setState((prev) => ({...prev, selected: option, errors: {}, message: null}));
    };

    /**
     * לאן הולכים אחרי שהרישום הושלם.
     *
     * שני יעדים ולא אחד: ההרשמה ממשיכה למסך "ברוכים הבאים", ואימות חובה שנכנס מכאן
     * צריך להגיע לממשק - זה מה שהלקוח ניסה לעשות מלכתחילה, והוא רק נעצר בדרך.
     *
     * **ולפני שמנחיתים - שואלים אם נשאר חוב.** המסך הזה משחרר שער אחד, ולסשן יכול
     * להיות שער שני: לקוח שהגיע מההרשמה, או שהריסיילר הדליק לו דרישה בזמן שהמסך היה
     * פתוח, עדיין חייב לאמת אמצעי תקשורת - והנחיתה בממשק הייתה מסתיימת בדחייה על כל
     * קריאה, בלי שום הודעה שמסבירה למה. אותה תקלה בדיוק, ובכיוון ההפוך, הייתה במסך
     * אימות אמצעי התקשורת.
     *
     * `blockedSessionRoute` בוחר את היעד באותו סדר קבוע שכל שאר המסלולים משתמשים בו,
     * וכשאין חוב `landAfterLogin` מכבד גם `redirect_from` וגם את ממשק ברירת המחדל של
     * הריסיילר.
     */
    const leaveEnrollment = async () => {
        if (!afterLogin) {
            navigate("/congratulations");
            return;
        }
        const route = blockedSessionRoute(await checkIsTokenValid());
        if (route && route !== window.location.pathname) {
            navigate(route, {state: {afterLogin: true}});
            return;
        }
        landAfterLogin(userToken(), navigate, "/successfully-logged-in");
    };

    // Recovery codes are returned only for the first enrolled method.
    const finishEnrollment = (recoveryCodes) => {
        if (recoveryCodes.length === 0) {
            leaveEnrollment();
            return;
        }
        setState((prev) => ({
            ...prev,
            phase: PHASE_RECOVERY,
            recoveryCodes: recoveryCodes,
            errors: {},
            message: null,
        }));
    };

    // Enroll a preverified contact directly, or start the normal code flow.
    const handleContinueClick = async (e) => {
        e.preventDefault();
        if (sendRetry.isWaiting) {
            return;
        }
        const selected = state.selected;
        if (!selected) {
            setState((prev) => ({...prev, errors: {selected: 'Please choose a verification method'}}));
            return;
        }
        if (!beginSubmit()) {
            return;
        }

        try {
            if (selected.preVerified) {
                const data = {methodType: selected.methodType, destination: selected.destination};
                const reply = await axios.post(API_URL + 'enrollVerified2faMethod', data, requestConfig());
                if(reply?.data?.responseStatus === API_OK){
                    finishEnrollment(reply.data.recoveryCodes ?? []);
                    return;
                }
                // The grace window may have expired since options were loaded.
                // Fall back to the normal code flow.
                if(reply?.data?.message !== 'contact_not_verified'){
                    showReplyError(reply);
                    return;
                }
            }

            if (selected.methodType === PASSKEY_METHOD) {
                // The ceremony finishes the enrolment; there is no code step.
                let reply;
                try {
                    reply = await registerPasskey(requestConfig());
                } catch (ceremonyError) {
                    setState((prev) => ({
                        ...prev,
                        errors: {passkey: passkeyErrorMessage(ceremonyError)},
                        message: null,
                    }));
                    return;
                }
                if(reply?.data?.responseStatus === API_OK){
                    finishEnrollment(reply.data.recoveryCodes ?? []);
                    return;
                }
                showReplyError(reply, sendRetry);
                return;
            }

            if (selected.methodType === APP_METHOD) {
                const reply = await axios.get(API_URL + 'getQRCode', requestConfig());
                if(reply?.data?.responseStatus === API_OK){
                    setState((prev) => ({
                        ...prev,
                        phase: PHASE_VERIFY,
                        qrCode: reply.data.qrCode,
                        methodUuid: reply.data.methodUuid,
                        code: "",
                        errors: {},
                        message: null,
                    }));
                    return;
                }
                showReplyError(reply);
                return;
            }

            const data = {methodType: selected.methodType, destination: selected.destination};
            const reply = await axios.post(API_URL + 'addVerificationMethod', data, requestConfig());
            if(reply?.data?.responseStatus === API_OK){
                setState((prev) => ({
                    ...prev,
                    phase: PHASE_VERIFY,
                    methodUuid: reply.data.methodUuid,
                    qrCode: "",
                    code: "",
                    errors: {},
                    message: "Verification code sent",
                }));
                return;
            }
            showReplyError(reply, sendRetry);
        } catch (e) {
            showRequestError();
        } finally {
            endSubmit();
        }
    };

    const handleVerifyClick = async (e) => {
        // אופציונלי, כי המילוי האוטומטי מגיש בלי אירוע. ראה `useWebOtp`.
        e?.preventDefault();
        if (verifyRetry.isWaiting) {
            return;
        }
        if (!state.code.trim()) {
            setState((prev) => ({...prev, errors: {code: 'Verification Code is required'}, message: null}));
            return;
        }
        if (!beginSubmit()) {
            return;
        }

        const isApp = state.selected?.methodType === APP_METHOD;
        const endpoint = isApp ? 'turnOnApp2fa' : 'verify2faMethod';
        const data = isApp
            ? {code: state.code.trim()}
            : {methodUuid: state.methodUuid, code: state.code.trim()};

        try {
            const reply = await axios.post(API_URL + endpoint, data, requestConfig());
            if(reply?.data?.responseStatus === API_OK){
                finishEnrollment(reply.data.recoveryCodes ?? []);
                return;
            }
            showReplyError(reply, verifyRetry);
        } catch (e) {
            showRequestError();
        } finally {
            endSubmit();
        }
    };

    // Reusing the unverified method sends a fresh code without creating a duplicate.
    const handleResendCode = async (e) => {
        e.preventDefault();
        if (sendRetry.isWaiting) {
            return;
        }
        const selected = state.selected;
        if (!selected || selected.methodType === APP_METHOD) {
            return;
        }
        if (!beginSubmit()) {
            return;
        }

        const data = {methodType: selected.methodType, destination: selected.destination};
        try {
            const reply = await axios.post(API_URL + 'addVerificationMethod', data, requestConfig());
            if(reply?.data?.responseStatus === API_OK){
                setState((prev) => ({
                    ...prev,
                    methodUuid: reply.data.methodUuid,
                    errors: {},
                    message: "Verification code sent",
                }));
                // קוד חדש לאותו שדה - והסמן חוזר אליו מהקישור שבתחתית המסך.
                focusCode();
                return;
            }
            showReplyError(reply, sendRetry);
        } catch (e) {
            showRequestError();
        } finally {
            endSubmit();
        }
    };

    const handleChange = (e) => {
        setState((prev) => ({...prev, [e.target.id]: e.target.value, errors: {}, message: null}));
    };

    const handleBackToChoose = (e) => {
        e.preventDefault();
        if (submittingRef.current) {
            return;
        }
        sendRetry.reset();
        setState((prev) => ({
            ...prev,
            phase: PHASE_CHOOSE,
            code: "",
            qrCode: "",
            methodUuid: "",
            errors: {},
            message: null,
        }));
    };

    const handleSkipClick = (e) => {
        if (submittingRef.current) {
            return;
        }
        // גם כאן ולא רק בהסתרת הכפתור: המסך מצייר לפי `require2fa`, וזו שכבה שנייה
        // למקרה שמישהו יקרא לפעולה בדרך אחרת. השער האמיתי הוא בשרת.
        if (require2fa) {
            return;
        }
        // אותה יציאה של סוף הרישום, ומאותה סיבה: מי שנכנס לכאן מהתחברות הולך לממשק,
        // ולא למסך "החשבון נוצר" של ההרשמה - הוא לא יצר עכשיו חשבון, הוא נכנס לשלו.
        leaveEnrollment();
    };

    /**
     * יציאה מהמסך, בלי להוסיף שיטה.
     *
     * **בלעדיה זהו מסך ללא מוצא.** כשהאימות הדו-שלבי הוא חובה אין "דלג", והסשן שבידי
     * הלקוח אינו פותח דבר עד שנרשמה שיטה - כלומר מי שנכנס לכאן ואינו יכול להשלים את
     * הרישום (המכשיר אינו בידיו, הדואר לא מגיע) נשאר תקוע בו, וגם רענון מחזיר אותו
     * לאותו מקום. היציאה היא **התנתקות** ולא ניווט: להשאיר סשן פתוח ולחזור למסך
     * ההתחברות פירושו לחזור לכאן בלחיצה הבאה.
     *
     * `/login` ולא מסך מסוים: הניתוב מכריע לפי הריסיילר, ולכן ריסיילר סלולרי נוחת
     * במסך שלו - ראה `RedirectByReseller`.
     */
    const handleCancelClick = (e) => {
        e.preventDefault();
        if (submittingRef.current) {
            return;
        }
        dispatch(logout());
        navigate("/login", { replace: true });
    };

    const handleRecoveryDoneClick = (e) => {
        e.preventDefault();
        leaveEnrollment();
    };

    const renderErrors = () => (
        Object.entries(state.errors).map(([key, msg]) => (
            <h2 key={key} className='mt20 error'><Trans i18nKey={msg} /></h2>
        ))
    );

    const renderChoose = () => (
        <>
            <h2>{t("Two-factor authentication adds an extra layer of protection for your account.")}</h2>
            <h2>{t("Choose how you would like to receive your login codes.")}</h2>

            {state.options.map((option) => (
                <div
                    key={option.methodType}
                    className={state.selected?.methodType === option.methodType ? 'mt20 input blue-border selected pointer' : 'mt20 input blue-border pointer'}
                    style={inFlightStyle}
                    onClick={() => selectOption(option)}
                >
                    {/*
                        המשפט כולו בתוך `option-label` אחד.

                        ה-`h2` הוא מיכל `flex` (הוא מרכז את התווית לגובה השורה), ומיכל
                        `flex` מפרק את התוכן שלו לפריטים: הטקסט היה פריט אחד והיעד -
                        `<span className="ltr-span">` - פריט שני, והרווח שביניהם נחתך
                        כרווח בקצה שורה. כך נולד "Email toname@example.com". פריט אחד
                        מחזיר את הטקסט לזרימה רגילה, והרווח חוזר איתו. בעברית זה לא
                        נראה, כי שם המשפט הוא "אימייל ל-" ואין בו רווח מלכתחילה.
                    */}
                    <h2 className='teal'>
                        <span className='option-label'>
                            <Trans
                                i18nKey={METHOD_LABEL[option.methodType]}
                                values={{destination: option.destination}}
                                components={LTR_VALUE}
                            />
                            {/*
                                יעד שכבר אומת בפרטי החשבון נרשם בלי קוד נוסף - ראה
                                `handleContinueClick`. נאמר כאן, כי אחרת "המשך" מסיים
                                את הרישום מיד והלקוח ממתין למסרון שלא נשלח.

                                **הניסוח חיובי ולא שולל**: "זמין לשימוש מיידי" ולא "לא
                                יישלח קוד". זו אותה עובדה, אבל השנייה קוראת כמו מגבלה
                                על האפשרות שנבחרה - בזמן שזו דווקא המהירה מבין השלוש.
                                "מיידי" גם אומר את מה שצריך להיאמר כאן: אין למה לחכות.

                                והוא ניטרלי לערוץ, כי ההינט הזה מופיע על מסרון, על שיחה
                                ועל אימייל - "המספר" היה שגוי על כתובת מייל.
                            */}
                            {option.preVerified && (
                                <span className='option-hint'>{t("Verified on your account - ready to use right away")}</span>
                            )}
                        </span>
                    </h2>
                </div>
            ))}

            {state.options.length === 0
                ? <h2 className='mt20'>{t("No verification method is available for your account right now.")}</h2>
                : <div className={`mt40 button button-space-between button-teal${state.submitting ? ' is-busy' : ''}`} style={sendingStyle} aria-busy={state.submitting ? 'true' : undefined} onClick={(e) => handleContinueClick(e)}>  <div className='mr20'>{state.submitting ? t("Working...") : t("Continue")}</div> {state.submitting ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>}

            {/*
                יציאה מהמתנה למכשיר, בזמן שחלון המאמת פתוח.

                נדרש כי חלון מבוטל אינו תמיד נדחה מעצמו - ראה `cancelPasskeyCeremony`.
                מוצג רק כשהשיטה הנבחרת היא מפתח גישה ורק בזמן שהפעולה בעבודה, ובלי
                `sendingStyle` (שמכבה את הלחיצות) - אחרת זה היה הדבר היחיד שאי אפשר
                ללחוץ עליו בדיוק כשהוא נדרש.
            */}
            {state.selected?.methodType === PASSKEY_METHOD && state.submitting && (
                <h2 className='mt20 teal pointer' onClick={() => cancelPasskeyCeremony()}>
                    {t("Cancel the passkey prompt")}
                </h2>
            )}

            {/*
                "דלג" - רק כשאפשר לדלג.

                אצל ריסיילר שמחייב אימות דו-שלבי אין כאן דילוג, ובמקומו נאמר במפורש
                למה: כפתור שמוביל למסך שייחסם הוא כפתור שמלמד את הלקוח שהמסך מקולקל.
                ראה `require2fa` למעלה, ואת השער בשרת.
            */}
            {require2fa
                ? <h2 className='mt20'>{t("Two-factor authentication is required on this account, so a method must be added to continue.")}</h2>
                : <div className='mt20 button button-space-between button-darkteal' style={inFlightStyle} onClick={(e) => handleSkipClick(e)}>  <div className='mr20'>{t("Skip")}</div> <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/> </div>}

            {/* היציאה. תמיד, ולא רק כשהאימות חובה - ראה `handleCancelClick`. */}
            <h2 className='mt20 teal pointer' style={inFlightStyle} onClick={(e) => handleCancelClick(e)}>{t("Cancel and return to the login screen")}</h2>

            {/* Show each unavailable reason once. */}
            {[...new Set(state.unavailable.map((item) => item.reason))].map((reason) => (
                <h2 key={reason} className='mt20'><Trans i18nKey={reason} /></h2>
            ))}

            {renderErrors()}
        </>
    );

    const renderVerify = () => {
        const isApp = state.selected?.methodType === APP_METHOD;
        /*
         * שיחה אינה "נשלחה אליך" - היא יוצאת עכשיו, ואין בה מה לפתוח.
         *
         * המשפט המשותף ("שלחנו קוד אל...") נכון למסרון ולדואר, ובשיחה הוא שולח את
         * הלקוח לחפש הודעה שלא תגיע. במקומו מוצגת האזהרה שמסבירה מהו הקוד.
         *
         * מוצג רק בשלב הזה: `renderVerify` נקרא רק כש-`phase` הוא `PHASE_VERIFY`,
         * כלומר אחרי שהשיחה כבר יצאה, ו"בחירת שיטה אחרת" מחזירה ל-`PHASE_CHOOSE`
         * ומסירה אותו.
         */
        const isVoice = state.selected?.methodType === VOICE_METHOD;
        return (
            <>
                {isApp
                    ? <>
                        <h2>{t("Scan this code with your authenticator app, then enter the code it shows.")}</h2>
                        {state.qrCode?<img src={state.qrCode} className='mt20 qr-code' alt={t("Authenticator App")}/>:<></>}
                      </>
                    : isVoice
                        ? <VoiceCallNotice phone={state.selected?.destination} />
                        : <h2><Trans i18nKey="We just sent a verification code to <span>{{destination}}</span>. Please insert it here" values={{destination: state.selected?.destination}} components={LTR_VALUE} /></h2>}

                <div className='mt30 input blue-border'><input ref={codeRef} className={state.errors.code?"error":""} type="text" id="code" inputMode="numeric" autoComplete="one-time-code" value={state.code} placeholder={isVoice ? t("Last 4 digits") : t("Verification Code")} onChange={(e) => handleChange(e)} /></div>
                <div className={`button button-space-between button-teal${state.submitting ? ' is-busy' : ''}`} style={verifyingStyle} aria-busy={state.submitting ? 'true' : undefined} onClick={(e) => handleVerifyClick(e)}>  <div className='mr20'>{state.submitting ? t("Checking the code...") : t("next")}</div> {state.submitting ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>

                {isApp
                    ? <></>
                    : <h2 className='mt40 teal pointer' style={sendingStyle} onClick={(e) => handleResendCode(e)}>{state.submitting ? <>{t("Sending...")}<span className='button-spinner' /></> : <Trans i18nKey="I didn't receive the code, <strong>please resend it</strong>" />}</h2>}

                <h2 className='mt20 teal pointer' style={inFlightStyle} onClick={(e) => handleBackToChoose(e)}>{t("Choose a different method")}</h2>
                <h2 className='mt20 teal pointer' style={inFlightStyle} onClick={(e) => handleCancelClick(e)}>{t("Cancel and return to the login screen")}</h2>

                {state.message?<h2 className='teal'><Trans i18nKey={state.message} /></h2>:""}
                {renderErrors()}
            </>
        );
    };

    const renderRecovery = () => (
        <>
            <h2>{t("Two-factor authentication is now on.")}</h2>

            {/* ההסבר, השמירה וההצגה - כולם ב-`RecoveryCodes`, וזהים למסך שחזור הסיסמה. */}
            <RecoveryCodes codes={state.recoveryCodes} />

            <div className='mt20 button button-space-between button-teal' onClick={(e) => handleRecoveryDoneClick(e)}>  <div className='mr20'>{t("next")}</div> <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/> </div>

            {state.message?<h2 className='mt20 teal'><Trans i18nKey={state.message} /></h2>:""}
            {renderErrors()}
        </>
    );

    /**
     * Enter ממשיך לשלב הבא - ולכל שלב הפעולה שלו.
     *
     * שלושת השלבים מצוירים באותו מסך ולא בשלושה נתיבים, ולכן גם ההמשך הוא אחד
     * שיודע היכן הוא עומד: בחירת שיטה שולחת קוד, שלב הקוד מאמת אותו, ומסך קודי
     * השחזור מסיים. ראה `useEnterSubmit`.
     */
    useEnterSubmit(
        (e) => {
            if (state.phase === PHASE_VERIFY) return handleVerifyClick(e);
            if (state.phase === PHASE_RECOVERY) return handleRecoveryDoneClick(e);
            return handleContinueClick(e);
        },
        !state.submitting
    );

    const renderPhase = () => {
        if (state.phase === PHASE_VERIFY) {
            return renderVerify();
        }
        if (state.phase === PHASE_RECOVERY) {
            return renderRecovery();
        }
        return renderChoose();
    };

    return (
            <Frame>
                <div className='main main-two-step-verification'>
                    <div className='main-top'>
                        <h1>{t("Two-step verification")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                {renderPhase()}
                                <RetryNotice retry={sendRetry} i18nKey="A new code can be sent in {{time}}" />
                                <RetryNotice retry={verifyRetry} />
                            </div>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-pointing.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
    );
}
